import { mediaContent } from '@/mocks/homeData';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const categoryColorMap: Record<string, { bg: string; text: string; border: string }> = {
  수사기관: { bg: 'bg-primary-100', text: 'text-primary-700', border: 'border-primary-200' },
  법무기관: { bg: 'bg-accent-100', text: 'text-accent-700', border: 'border-accent-200' },
  공공기관: { bg: 'bg-secondary-100', text: 'text-secondary-700', border: 'border-secondary-200' },
  교육기관: { bg: 'bg-primary-100', text: 'text-primary-700', border: 'border-primary-200' },
  지원기관: { bg: 'bg-accent-100', text: 'text-accent-700', border: 'border-accent-200' },
  방송출연: { bg: 'bg-accent-100', text: 'text-accent-800', border: 'border-accent-300' },
};

export default function MediaSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const { ref: broadcastRef, isVisible: broadcastVisible } = useScrollReveal(0.1);
  const { ref: educationRef, isVisible: educationVisible } = useScrollReveal(0.05);

  const broadcastActivities = mediaContent.activities.filter((a) => a.category === '방송출연');
  const educationActivities = mediaContent.activities.filter((a) => a.category !== '방송출연');

  return (
    <section id="media" className="relative py-20 md:py-28 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div
            ref={headerRef}
            className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="inline-block px-4 py-1.5 rounded-full text-xs md:text-sm font-medium tracking-wider bg-primary-100 text-primary-700 mb-4">
              {mediaContent.sectionLabel}
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              {mediaContent.sectionTitle}
            </h2>
            <p className="mt-3 text-base md:text-lg text-foreground-600 max-w-xl mx-auto">
              {mediaContent.sectionSubtitle}
            </p>
          </div>

          {/* 방송 출연 — featured horizontal card */}
          {broadcastActivities.length > 0 && (
            <div
              ref={broadcastRef}
              className={`mb-16 transition-all duration-700 delay-100 ${
                broadcastVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <h3 className="text-sm font-semibold tracking-wider text-accent-700 mb-5 inline-flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-tv-2-line text-base" />
                </span>
                방송 출연
              </h3>

              {broadcastActivities.map((activity) => {
                const imageUrl = (activity as { image?: string }).image;
                return (
                  <div
                    key={activity.institution}
                    className="group flex flex-col md:flex-row rounded-2xl bg-background-50 border border-background-200/70 hover:border-accent-300/60 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                  >
                    {/* Image */}
                    {imageUrl && (
                      <div className="relative w-full md:w-[420px] h-52 md:h-auto shrink-0 overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={`${activity.institution} - ${activity.role}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent md:bg-gradient-to-r md:from-black/30 md:to-transparent" />
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex flex-col justify-center gap-4 p-6 md:p-8 flex-1">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 border border-accent-200 whitespace-nowrap">
                          방송출연
                        </span>
                        {activity.years.map((year) => (
                          <span
                            key={year}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border border-background-200"
                          >
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-calendar-line text-[10px]" />
                            </span>
                            {year}
                          </span>
                        ))}
                      </div>

                      <div>
                        <h4 className="text-xl md:text-2xl font-bold text-foreground-900 group-hover:text-accent-700 transition-colors duration-200">
                          {activity.institution}
                        </h4>
                        <p className="mt-2 text-sm md:text-base text-foreground-500 leading-relaxed">
                          {activity.role}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 교육·강의 — list layout */}
          {educationActivities.length > 0 && (
            <div
              ref={educationRef}
              className={`transition-all duration-700 delay-200 ${
                educationVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <h3 className="text-sm font-semibold tracking-wider text-primary-700 mb-5 inline-flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mic-line text-base" />
                </span>
                교육 · 강의
              </h3>

              <div className="divide-y divide-background-200/70 border border-background-200/70 rounded-2xl bg-background-50 overflow-hidden">
                {educationActivities.map((activity, index) => {
                  const colors = categoryColorMap[activity.category] ?? {
                    bg: 'bg-background-100',
                    text: 'text-foreground-700',
                    border: 'border-background-200',
                  };
                  return (
                    <div
                      key={activity.institution}
                      className="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 px-5 md:px-7 py-4 md:py-5 hover:bg-background-100/50 transition-colors duration-200"
                      style={{ transitionDelay: `${index * 50}ms` }}
                    >
                      {/* Left: icon + info */}
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0 mt-0.5">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className={`${activity.icon} text-lg`} />
                          </span>
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors duration-200">
                              {activity.institution}
                            </h3>
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${colors.bg} ${colors.text} ${colors.border} whitespace-nowrap`}
                            >
                              {activity.category}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-foreground-500 leading-relaxed">
                            {activity.role}
                          </p>
                        </div>
                      </div>

                      {/* Right: year badges */}
                      <div className="flex flex-wrap gap-1.5 sm:justify-end shrink-0 ml-14 sm:ml-0">
                        {activity.years.map((year) => (
                          <span
                            key={year}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border border-background-200"
                          >
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-calendar-line text-[10px]" />
                            </span>
                            {year}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
}